import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loader2 } from 'lucide-react';
import { chatLucideIconProps } from '../components/chat/chat-ui';
import { useParams } from 'react-router-dom';
import type { AssistantState } from '@/embedded/chat/types/runtime-state';
import type { ConversationMessage } from '@/embedded/chat/types/conversation-message';
import { ConversationThread } from '@/embedded/chat/components/messages/conversation-thread';
import {
  getContentBoxMinimapInsights,
  MinimapOptionsProvider,
} from '@/embedded/chat/components/minimap';
import { cn } from '@/utils/cn';
import { WebChatEventAdapter } from '../lib/chat-adapter';
import {
  fetchSharedChatMeta,
  streamSharedChat,
  type SharedChatMeta,
  type WebAgentRawSSEEvent,
} from '../lib/shared-chat-api';
import { AppLogo } from '@/assets/logos/app-logo';
import { useWebsiteTitlebar } from '../lib/titlebar';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; meta: SharedChatMeta };

const WEBSITE_CHAT_CONTENT_BOX_ID = 'website-chat-content-box';

function upsertAssistantState(
  current: AssistantState[],
  nextState: AssistantState
): AssistantState[] {
  const index = current.findIndex(
    state => state.sequence === nextState.sequence
  );
  if (index === -1) {
    return [...current, nextState].sort((a, b) => a.sequence - b.sequence);
  }

  const next = [...current];
  next[index] = nextState;
  return next;
}

function applyAssistantStateUpdate(
  current: AssistantState[],
  nextState: AssistantState | null
): AssistantState[] {
  if (nextState === null) {
    return [];
  }
  return upsertAssistantState(current, nextState);
}

function appendMessage(
  current: ConversationMessage[],
  next: ConversationMessage,
  options: { replaceOnly?: boolean } = {}
): ConversationMessage[] {
  const existingIndex = current.findIndex(m => m.id === next.id);
  if (existingIndex !== -1) {
    const updated = [...current];
    updated[existingIndex] = next;
    return updated;
  }
  if (options.replaceOnly) {
    return current;
  }
  return [...current, next];
}

export function SharedChatPage(): React.JSX.Element {
  const { publicId = '' } = useParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [assistantStateSequence, setAssistantStateSequence] = useState<
    AssistantState[]
  >([]);
  const [finishState, setFinishState] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const minimapOptions = useMemo(
    () => ({
      getInsights: () =>
        getContentBoxMinimapInsights(WEBSITE_CHAT_CONTENT_BOX_ID),
    }),
    []
  );
  const currentAssistantState =
    assistantStateSequence[assistantStateSequence.length - 1] ?? null;

  useWebsiteTitlebar({ sourceRef: pageRef });

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Only auto-scroll if user is near bottom
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (isNearBottom) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [assistantStateSequence, messages, scrollToBottom]);

  // Load metadata
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    fetchSharedChatMeta(publicId)
      .then(meta => {
        if (!cancelled) {
          setState({ status: 'ready', meta });
          document.title = meta.title
            ? `${meta.title} — Shared Chat`
            : 'Shared Chat';
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Failed to load shared chat',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [publicId]);

  // Connect to SSE stream
  useEffect(() => {
    if (state.status !== 'ready') return;

    const controller = new AbortController();
    const adapter = new WebChatEventAdapter();

    const handleEvent = (event: WebAgentRawSSEEvent) => {
      const output = adapter.process(event.eventType, event.data);

      if (output.conversationMessages.length > 0) {
        setMessages(current => {
          let next = current;
          const replaceOnly = event.eventType === 'message_updated';
          for (const msg of output.conversationMessages) {
            next = appendMessage(next, msg, { replaceOnly });
          }
          return next;
        });
      }

      if (output.assistantState !== undefined) {
        setAssistantStateSequence(current =>
          applyAssistantStateUpdate(current, output.assistantState ?? null)
        );
      }

      if (output.sessionFinished) {
        setFinishState(output.sessionFinished.state);
      }
    };

    streamSharedChat(publicId, controller.signal, handleEvent).catch(() => {
      // Stream ended or failed — ignore in shared view
    });

    return () => {
      controller.abort();
    };
  }, [publicId, state.status]);

  if (state.status === 'loading') {
    return (
      <MinimapOptionsProvider value={minimapOptions}>
        <div
          ref={pageRef}
          className="flex h-[100dvh] items-center justify-center overflow-hidden bg-background text-foreground"
        >
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 {...chatLucideIconProps} className="size-5 animate-spin" />
            <span>Loading shared chat…</span>
          </div>
        </div>
      </MinimapOptionsProvider>
    );
  }

  if (state.status === 'error') {
    return (
      <MinimapOptionsProvider value={minimapOptions}>
        <div
          ref={pageRef}
          className="flex h-[100dvh] items-center justify-center overflow-hidden bg-background text-foreground"
        >
          <div className="max-w-md text-center">
            <h1 className="text-lg font-medium">Shared chat not found</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state.message}
            </p>
          </div>
        </div>
      </MinimapOptionsProvider>
    );
  }

  const title = state.meta.title || 'Shared Chat';

  return (
    <MinimapOptionsProvider value={minimapOptions}>
      <div
        ref={pageRef}
        className="flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground md:bg-muted/35 md:p-4"
      >
        {/* Header */}
        <header className="flex items-center justify-between border-b border-border/70 bg-background px-4 py-3 md:mx-auto md:w-full md:max-w-[920px] md:rounded-t-[20px] md:border md:border-b-0 md:border-border/70 md:px-5 md:py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <AppLogo className="size-6 shrink-0" />
            <div className="min-w-0">
              <div className="chat-type-body-strong truncate">{title}</div>
              <div className="chat-type-caption text-muted-foreground">
                Shared chat
              </div>
            </div>
          </div>

          <span className="chat-type-caption inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-2.5 py-1 text-muted-foreground">
            <span
              className={cn(
                'size-2 rounded-full',
                finishState === 'waiting'
                  ? 'bg-amber-500'
                  : finishState
                    ? 'bg-emerald-500'
                    : 'bg-neutral-400'
              )}
            />
            {finishState === 'waiting'
              ? 'Waiting'
              : finishState === 'completed' || finishState === 'cancelled'
                ? 'Ended'
                : finishState === 'failed'
                  ? 'Failed'
                  : 'Live'}
          </span>
        </header>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto bg-background px-4 py-4 md:mx-auto md:w-full md:max-w-[920px] md:border-x md:border-border/70 md:px-5"
        >
          <div
            id={WEBSITE_CHAT_CONTENT_BOX_ID}
            className="mx-auto flex w-full max-w-[920px] flex-col gap-3.5"
          >
            {messages.length === 0 && assistantStateSequence.length === 0 ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2
                    {...chatLucideIconProps}
                    className="size-4 animate-spin"
                  />
                  <span className="chat-type-body">Loading conversation…</span>
                </div>
              </div>
            ) : (
              <ConversationThread
                messages={messages}
                assistantStateSequence={assistantStateSequence}
                isStreaming={
                  currentAssistantState
                    ? currentAssistantState.phase !== 'idle'
                    : false
                }
                showMessageIdentity={false}
                className="gap-3.5"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-border/70 bg-background px-4 py-3 text-center md:mx-auto md:w-full md:max-w-[920px] md:rounded-b-[20px] md:border md:border-t-0 md:border-border/70 md:px-5">
          <span className="chat-type-caption text-muted-foreground">
            Shared with Bridge
          </span>
        </footer>
      </div>
    </MinimapOptionsProvider>
  );
}
